import { set } from 'lodash'
import {
  addOrUpdateRouterWithPattern,
  isClassicEditor,
} from '@wix/wix-data-client-common'
import { ensurePageIsLegacyIndexable } from '../../ensurePageIsLegacyIndexable'
import { makeItemPageNonIndexable } from '../../makeItemPageNonIndexable'

import type { HttpClient } from '@wix/http-client'
import type { EditorSDK } from '@wix/wix-data-client-common-standalone'
import type {
  AppApi,
  EDITOR_TYPE,
  PageType,
  PatternDefinition,
} from '@wix/wix-data-client-common'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import type { SerializedPageStructure } from '@wix/document-services-types'
import { addRouterDataset } from './addRouterDataset'

export type CreatePreset = (params: {
  collectionName: string
  routerId: string
}) => SerializedPageStructure

interface CreateOrConnectDynamicPageParams {
  managingAppDefId: string
  editorSdkProxy: EditorSDK
  appApi: AppApi
  prefix: string
  pattern: string
  pageType: PageType
  pageRef: PageRef | undefined
  createPreset: CreatePreset
  routeDefinition: PatternDefinition
  pageTitle: string
  isEditPage: boolean
  collectionName: string
  editorType: EDITOR_TYPE
  httpClient: HttpClient
}

export const createOrConnectDynamicPage = async ({
  managingAppDefId,
  editorSdkProxy,
  appApi,
  prefix,
  pattern,
  pageType,
  pageRef,
  createPreset,
  routeDefinition,
  pageTitle,
  isEditPage,
  collectionName,
  editorType,
  httpClient,
}: CreateOrConnectDynamicPageParams) => {
  const routerRef = await addOrUpdateRouterWithPattern(
    editorSdkProxy,
    prefix,
    pattern,
    routeDefinition,
  )
  const router = await editorSdkProxy.routers.get({ routerRef })

  if (pageRef) {
    await editorSdkProxy.document.pages.data.update({
      pageRef,
      data: { parentPageId: undefined },
    })
  } else {
    const definition = set(
      createPreset({ collectionName, routerId: router.id }),
      'data.pageUriSEO',
      '',
    )

    pageRef = await editorSdkProxy.document.pages.add({
      title: pageTitle,
      definition,
      shouldAddMenuItem: true,
      shouldNavigateToPage: false,
    })

    if (isClassicEditor(editorType)) {
      const controllersInPage =
        await editorSdkProxy.controllers.listControllers({
          pageRef,
          includeTPAWidget: true,
        })

      const { controllerRef } = controllersInPage[0]

      await editorSdkProxy.document.controllers.moveAppControllerToPage({
        controllerRef,
        pageRef,
      })
    }
  }

  await Promise.all([
    editorSdkProxy.routers.pages.connect({
      routerRef,
      pageRef,
      pageRoles: [routeDefinition.pageRole],
    }),
    editorSdkProxy.pages.rename({ pageRef, title: pageTitle }),
  ])

  if (isEditPage) {
    await makeItemPageNonIndexable({ httpClient, pageId: pageRef.id })
  } else {
    await ensurePageIsLegacyIndexable({
      editorSdkProxy,
      pageRef,
    })
  }

  if (!createPreset) {
    await addRouterDataset({
      editorSdkProxy,
      appApi,
      editorType,
      pageRef,
      collectionName,
      pageType,
    })
  }

  await editorSdkProxy.document.pages.data.update({
    pageRef,
    data: { managingAppDefId },
  })

  return { pageRef }
}
